/**
 * An item's colours, which are several things stored in one field.
 *
 * A thing is often two colours and sometimes three, and `color` has always been
 * one free-text box -- so "Orange/White" was already being typed into it. Read
 * as a single value that is exactly what it looks like: a third colour, filed
 * beside Orange and White, matching neither when either is asked for.
 *
 * So the field is read as a delimited set rather than a string. Nothing about
 * the event log changes, which is the point: the log is append-only, every
 * `item.fieldSet` carrying a colour is permanent, and the values already in it
 * are *already in the target format*. A reading changes what they mean without
 * anything having to be rewritten or migrated.
 *
 * The cost, stated: the whole set is one cell, so two devices each adding a
 * colour offline resolve by last-writer-wins and one of the two additions is
 * lost. Tags avoid that by being one event each. Colours are edited rarely and
 * from one device at a time, which is the trade -- and the alternative is a new
 * entity, a new event family and a legacy string that must be understood
 * forever.
 */
import { fieldValue } from './merge.js';
/**
 * What a set is joined with when stored.
 *
 * `/` because that is the convention that was already being used. The editor
 * writes this; nobody has to remember it.
 */
export const COLOR_SEPARATOR = '/';
/**
 * What a set is split on when read.
 *
 * Liberal on the way in, because the values already in the log were typed by
 * hand over months: a comma is as likely as a slash, and either plainly means
 * "and". Being strict here would file "Orange, White" as a colour of its own,
 * which is the bug this exists to fix.
 */
const SPLIT = /[/,]/;
/**
 * The colours in a stored value, in the order written.
 *
 * Deduplicated case-insensitively, keeping the first spelling: someone who
 * typed "white" after "White" meant one colour, and the catalog should not
 * offer both.
 */
export function splitColors(value) {
    if (typeof value !== 'string')
        return [];
    const seen = new Map();
    for (const part of value.split(SPLIT)) {
        const trimmed = part.trim();
        if (trimmed === '')
            continue;
        const key = trimmed.toLowerCase();
        if (!seen.has(key))
            seen.set(key, trimmed);
    }
    return [...seen.values()];
}
/**
 * The stored form of a set of colours.
 *
 * Returns null for an empty set rather than an empty string, because that is
 * what the field takes to mean "cleared" -- and `undefined` versus `null` is
 * the difference between never set and deliberately emptied.
 */
export function joinColors(values) {
    const joined = splitColors(values.join(COLOR_SEPARATOR));
    return joined.length === 0 ? null : joined.join(COLOR_SEPARATOR);
}
/** The colours on an item. */
export const colorsOf = (item) => splitColors(fieldValue(item, 'color'));
/** Whether an item carries a colour, whatever else it carries beside it. */
export const hasColor = (item, wanted) => {
    const want = wanted.trim().toLowerCase();
    return colorsOf(item).some((color) => color.toLowerCase() === want);
};
//# sourceMappingURL=colors.js.map